import { select, all, call, take, put, fork, takeEvery, takeLatest } from 'redux-saga/effects';
import { getJSON, patchJSON, postJSON, params } from '../../util';

/* eslint-disable complexity */
export function* watchUpdateSettings() {
    while (yield take('member:update-settings')) {
        const [user, settings] = yield all([
            select(state => state.user),
            select(state => state.settings),
        ]);

        if (user.logged_in && user.member_uuid && settings.fields) {
            let error = false;

            delete settings.errors.general;

            yield put({
                type: 'settings:update',
                payload: {
                    errors: settings.errors,
                    error: settings.errors.length,
                    submitting: true,
                },
            });

            if (settings.fields.old_password && settings.fields.new_password) {
                const pw_response = yield call(postJSON, '/api/member/password-reset', {
                    old_password: settings.fields.old_password,
                    new_password: settings.fields.new_password,
                });

                if (pw_response && pw_response.error) {
                    error = true;
                    yield put({
                        type: 'settings:update',
                        payload: {
                            errors: {
                                new_password: pw_response.error,
                            },
                            error: 1,
                        },
                    });
                }
            }

            if (!error) {
                const api = yield select(state => state.api);
                let fields = settings.fields;

                if (!fields.sexual_orientation) {
                    fields.sexual_orientation = user.sexual_orientation;
                }

                delete fields.confirm_new_password;

                fields.partner = api.params.partner;
                fields.key = api.params.key;

                if (fields.email && fields.email != user.email) { //no error, attempt to update email first
                    try {
                        yield call(patchJSON, `${api.host}/member/member/${user.member_uuid}`, {
                            email: fields.email,
                            key: fields.key,
                            partner: fields.partner,
                        });
                    } catch (err) {
                        error = true;
                        yield put({
                            type: 'settings:update',
                            payload: {
                                errors: {
                                    email: 'This email address is already in use',
                                },
                                error: 1,
                            },
                        });
                    }
                }

                delete fields.email;

                if (!error && fields.forum_name && fields.forum_name != user.forum_name) { //no error, update forum_name
                    try {
                        yield call(patchJSON, `${api.host}/member/member/${user.member_uuid}`, {
                            forum_name: fields.forum_name,
                            key: fields.key,
                            partner: fields.partner,
                        });
                    } catch (err) {
                        error = true;
                        yield put({
                            type: 'settings:update',
                            payload: {
                                errors: {
                                    forum_name: 'This User Name is not available',
                                },
                                error: 1,
                            },
                        });
                    }
                }

                delete fields.forum_name;

                if (!error && Object.keys(fields).length) { //no error, update everything else
                    try {
                        yield call(patchJSON, `${api.host}/member/member/${user.member_uuid}`, fields);
                    } catch (err) {
                        error = true;
                        yield put({
                            type: 'settings:update',
                            payload: {
                                errors: {
                                    general: err,
                                },
                                error: 1,
                            },
                        });
                    }
                }

                if (!error) {
                    yield all([
                        put({ type: 'member:get-details' }),
                        put({ type: 'user:force-refresh' }),
                        put({
                            type: 'settings:update',
                            payload: {
                                fields: {},
                                errors: {},
                                error: 0,
                                dirty: 0,
                                submitting: false,
                                success: true,
                            },
                        }),
                    ]);
                } else {
                    yield put({ type: 'settings:update', payload: { submitting: false } });
                }
            } else {
                yield put({ type: 'settings:update', payload: { submitting: false } });
            }
        }
    }
}
/* eslint-enable complexity */

export function* watchGetDetails() {
    while(yield take('member:get-details')) {
        const response = yield call(getJSON, '/api/member/get-details');

        if (response && response.status && response.status == 'ok' && response.details) {
            yield put({
                type: 'user:update',
                payload: response.details,
            });
        }

        yield put({ type: 'user:force-refresh' });
    }
}

export function* passwordResetInit({ payload }) {
    yield put({ type: 'form:update', payload: {
        submitting: true,
        submitted: false,
        error: null,
    }});

    let result = null;
    try {
        result = yield call(postJSON, '/api/member/password-reset/init', params({ email: payload }));
    } catch (e) {
        result = e.responseJSON;
    }

    if (result && result.error) {
        yield put({ type: 'form:update', payload: {
            submitting: false,
            error: result.error,
        }});
    } else {
        yield put({ type: 'form:update', payload: {
            submitting: false,
            submitted: true,
        }});
    }
}

export function* passwordResetSet({ payload }) {
    yield put({ type: 'form:update', payload: {
        submitting: true,
        submitted: false,
        error: null,
    }});

    let result = null;
    try {
        result = yield call(postJSON, '/api/member/password-reset/set', params({
            key: payload.key,
            password: payload.password,
        }));
    } catch (e) {
        result = e.responseJSON;
    }

    if (result && result.error) {
        yield put({ type: 'form:update', payload: {
            submitting: false,
            error: result.error,
        }});
    } else {
        yield put({ type: 'form:update', payload: {
            submitting: false,
            submitted: true,
        }});
    }
}

export function* checkForumsUsername({ payload }) {
    let result;
    yield put({
        type: 'form:update',
        payload: {
            submitting: true,
        },
    });

    try {
        result = yield call(postJSON, '/api/forums/register', {
            forum_name: payload,
        });
    } catch (err) {
        result = err.responseJSON;
    }

    if (result && result.error) {
        yield all([
            put({
                type: 'form:update',
                payload: {
                    submitting: false,
                },
            }),
            put({ type: 'form:update', payload: {
                errors: { forum_name: 'This Username is not available' },
                error: true,
            }}),
        ]);
    } else {
        yield all([
            put({ type: 'user:force-refresh' }),
            put({
                type: 'form:update',
                payload: {
                    submitted: true,
                },
            }),
        ]);
    }
}

export function* root() {
    yield all([
        fork(watchUpdateSettings),
        fork(watchGetDetails),
        takeEvery('member:password-reset-init', passwordResetInit),
        takeEvery('member:password-reset-set', passwordResetSet),
        takeLatest('member:check-forums-username', checkForumsUsername),
    ]);
}
